import categoryService from '../../../services/categoryService';
import filterService from '../../../services/filterService';

import {
    setCategoryLoading,
    setCategoryError,
    setCategoryList,
    setCategoryCurrent,
    setCategoryPagination,
    setCategorySort,
    setCategoryFilters,
    setCategoryDefaultFilter,
    setCategorySettings,
    clearCategoryCurrent,
    clearCategoryError,
    setCategoryFilterLoading,
    changeCategoryFilterCondition,
    addCategoryFilterCondition,
    updateCategoryFilterConditionRule,
    updateCategoryFilterConditionValue,
    removeCategoryFilterCondition,
    resetCategoryFiltersAndPagination
} from './categorySlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().category.isSettingsInitialized) {
            dispatch(setCategoryLoading(true));
            const response = await categoryService.getSettings();

            // Set the settings
            dispatch(setCategorySettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setCategoryDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setCategoryError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setCategoryLoading(false));
    }
};

export const fetchCategories = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().category;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setCategoryLoading(true));
        const response = await categoryService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setCategoryList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchCategories:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setCategoryError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCategoryLoading(false));
    }
};

export const fetchCategory = (id) => async (dispatch) => {
    try {
        dispatch(setCategoryLoading(true));
        const response = await categoryService.getOne(id);
        dispatch(setCategoryCurrent(response.data));
    } catch (error) {
        dispatch(setCategoryError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setCategoryLoading(false));
    }
};

export const createCategory = (data) => async (dispatch) => {
    try {
        const response = await categoryService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateCategory = (id, data) => async (dispatch) => {
    try {
        const response = await categoryService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteCategory = (id) => async (dispatch) => {
    try {
        dispatch(setCategoryLoading(true));
        const response = await categoryService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setCategoryError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCategoryLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setCategoryPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setCategorySort(sort));
};

export const searchCategories = (query) => (dispatch, getState) => {
    const currentFilters = getState().category.filters;
    dispatch(setCategoryFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setCategoryDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeCategoryFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addCategoryFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateCategoryFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateCategoryFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeCategoryFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setCategoryFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().category.settings;
            dispatch(setCategorySettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setCategoryFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setCategoryFilterLoading(false));
        dispatch(setCategoryError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetCategoryFiltersAndPagination());
};